import { useEffect, useState } from 'react'
import { createClient } from '../util/supabase/component'
import type { OilContentForMoistureEquilibriumCalc } from '../types'
import {
  type QueryFunctionContext,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query'

const sortOilContent = (
  oc1: OilContentForMoistureEquilibriumCalc,
  oc2: OilContentForMoistureEquilibriumCalc,
) => {
  const species1 = `${oc1.species.genus} ${oc1.species.epithet}`
  const species2 = `${oc2.species.genus} ${oc2.species.epithet}`
  if (species1 > species2) return 1
  if (species1 < species2) return -1
  return 0
}

const queryFn = async (
  context: QueryFunctionContext<Array<string | number>>,
) => {
  const supabase = createClient()
  const page = context.queryKey[1] as number
  const rangeStart = page * 1000
  const { data, error } = await supabase
    .from('oil_content')
    .select(
      '*, species(id,genus,epithet,infraspecies_rank,infraspecies_epithet)',
      { count: 'exact' },
    )
    .range(rangeStart, rangeStart + 1000)

  if (error) throw new Error(error.message)
  if (!data) return

  const refIds = new Set(
    data.map((item) => item.reference_id).filter(Boolean) as number[],
  )
  const { data: refs } = await supabase
    .from('references')
    .select('*')
    .in('id', Array.from(refIds))

  const refsMap = Object.fromEntries(
    refs ? refs.map((ref) => [ref.id, ref]) : [],
  )
  // interpolate references
  const dataWithRefs = data.map((d) => {
    const ref = d.reference_id ? refsMap[d.reference_id] : undefined
    return { ...d, reference: ref }
  })
  return dataWithRefs as OilContentForMoistureEquilibriumCalc[]
}

export const useOilContent = () => {
  const [page, setPage] = useState(0)
  const { data, error, isPlaceholderData, isLoading } = useQuery({
    queryKey: ['oil_content', page],
    queryFn,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (
      !isLoading &&
      !isPlaceholderData &&
      !error &&
      data?.length &&
      data.length % 1000 === 0
    )
      setPage((page) => page + 1)
  }, [isLoading, isPlaceholderData, data, error])

  return data?.sort(sortOilContent)
}
